import { ReactNode, useMemo } from 'react';
import BigNumber from 'bignumber.js';
import {
  Paper,
  SxProps,
  Theme,
  Typography,
  TypographyOwnProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Chart } from './components/Chart';
import { NoDataGuard, useNoDataContainerStyles } from '../NoDataGuard';
import { PieChartData } from './types';
import { Title } from '../Title';
import { useBasePieChartStyles } from './BasePieChartStyles';
import { BaseTable } from '../BaseTable';
import { getColor } from './utils/getColor';

export interface BasePieChartProps {
  NoDataPlaceholder?: typeof NoDataGuard;
  amount: string;
  amountTitle?: string;
  className?: string;
  data: PieChartData[];
  dataTableTitles: string[] | JSX.Element[];
  dataTableTypographyVariant?: TypographyOwnProps['variant'];
  formatValue?: (value: number) => string;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  title?: string;
  csvDownloadButton?: ReactNode;
}

export const BasePieChart = ({
  NoDataPlaceholder = NoDataGuard,
  amount,
  amountTitle,
  className,
  data,
  isLoading,
  sx,
  title,
  dataTableTitles,
  dataTableTypographyVariant = 'caption',
  formatValue = value => new BigNumber(value).toFormat(0),
  csvDownloadButton,
}: BasePieChartProps) => {
  const { classes, cx } = useBasePieChartStyles();
  const {
    classes: { container },
  } = useNoDataContainerStyles();

  const theme = useTheme();

  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));

  const tableData = useMemo(
    () =>
      data.map((item, index) => ({
        label: (
          <Typography
            title={item.name}
            variant={dataTableTypographyVariant}
            className={classes.tableItemName}
          >
            <span
              className={classes.dot}
              style={{ backgroundColor: getColor(index) }}
            />
            <span className={classes.tableItemNameText}>{item.name}</span>
          </Typography>
        ),
        value: formatValue(item.requestsCount),
      })),
    [
      classes.dot,
      classes.tableItemName,
      classes.tableItemNameText,
      data,
      dataTableTypographyVariant,
      formatValue,
    ],
  );

  return (
    <Paper
      sx={sx}
      className={cx(classes.root, className, {
        [container]: data.length === 0,
      })}
    >
      <div className={classes.titleContainer}>
        {title && <Title className={classes.title}>{title}</Title>}
        {csvDownloadButton && csvDownloadButton}
      </div>
      <NoDataPlaceholder data={data} isLoading={isLoading}>
        <div className={classes.content}>
          {!isLgDown && (
            <div className={classes.chartWrapper}>
              <Chart
                className={classes.chart}
                amount={amount}
                amountTitle={amountTitle}
                data={data}
                dataTableTypographyVariant={dataTableTypographyVariant}
              />
            </div>
          )}

          <BaseTable
            className={classes.chartDataTable}
            isLoading={isLoading}
            dataTableTypographyVariant={dataTableTypographyVariant}
            data={tableData}
            headingTitles={dataTableTitles}
          />
        </div>
      </NoDataPlaceholder>
    </Paper>
  );
};
